const DATETIME_LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;
const partNumber = (parts, type) => { var _a; return Number((_a = parts.find((part) => part.type === type)) === null || _a === void 0 ? void 0 : _a.value); };
/** Milliseconds to add to UTC to get wall time in `timeZone` at `utcMs`. */
const tzOffsetMs = (utcMs, timeZone) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(new Date(utcMs));
    const hour = partNumber(parts, 'hour');
    const asUtc = Date.UTC(partNumber(parts, 'year'), partNumber(parts, 'month') - 1, partNumber(parts, 'day'), hour === 24 ? 0 : hour, partNumber(parts, 'minute'), partNumber(parts, 'second'));
    return asUtc - utcMs;
};
const pad2 = (value) => String(value).padStart(2, '0');
/**
 * Interpret a datetime-local wall clock (`YYYY-MM-DDTHH:mm`) in `timeZone`
 * and return a UTC ISO string.
 */
export const zonedDateTimeToUtcIso = (local, timeZone) => {
    var _a;
    if (!local)
        return undefined;
    const match = DATETIME_LOCAL_RE.exec(local);
    if (!match) {
        const parsed = new Date(local);
        return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
    }
    const utcGuess = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]), Number((_a = match[6]) !== null && _a !== void 0 ? _a : 0));
    try {
        const offset = tzOffsetMs(utcGuess, timeZone);
        const utcWithOffset = utcGuess - offset;
        const offsetAtResult = tzOffsetMs(utcWithOffset, timeZone);
        const utc = offsetAtResult === offset ? utcWithOffset : utcGuess - offsetAtResult;
        return new Date(utc).toISOString();
    }
    catch {
        return new Date(utcGuess).toISOString();
    }
};
/** Format a UTC ISO instant as a datetime-local string in `timeZone`. */
export const utcIsoToZonedDateTime = (iso, timeZone) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return '';
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
        }).formatToParts(date);
        const hour = partNumber(parts, 'hour');
        const y = partNumber(parts, 'year');
        const month = pad2(partNumber(parts, 'month'));
        const day = pad2(partNumber(parts, 'day'));
        const hh = pad2(hour === 24 ? 0 : hour);
        const mm = pad2(partNumber(parts, 'minute'));
        return `${y}-${month}-${day}T${hh}:${mm}`;
    }
    catch {
        return '';
    }
};
/**
 * Keep the wall-clock time of `iso` (as seen in `fromTimeZone`) and express
 * that same clock time in `toTimeZone`.
 */
export const reinterpretIsoInTimeZone = (iso, fromTimeZone, toTimeZone) => {
    if (!iso)
        return undefined;
    if (fromTimeZone === toTimeZone)
        return iso;
    const wall = utcIsoToZonedDateTime(iso, fromTimeZone);
    return wall ? zonedDateTimeToUtcIso(wall, toTimeZone) : iso;
};
/** Build a Date whose local components match `iso` as seen in `timeZone`. */
export const utcIsoToWallClockDate = (iso, timeZone) => {
    const local = utcIsoToZonedDateTime(iso, timeZone);
    if (!local)
        return new Date(iso);
    const [datePart, timePart] = local.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    return new Date(year, month - 1, day, hour, minute);
};
/** Treat `date`'s local components as wall time in `timeZone`. */
export const wallClockDateToUtcIso = (date, timeZone) => {
    var _a;
    const local = `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
    return (_a = zonedDateTimeToUtcIso(local, timeZone)) !== null && _a !== void 0 ? _a : date.toISOString();
};
export const formatEventWhen = (start, options) => {
    const timeZone = (options === null || options === void 0 ? void 0 : options.timeZone) || undefined;
    const dateOptions = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        ...(timeZone ? { timeZone } : {}),
    };
    if (options === null || options === void 0 ? void 0 : options.allDay) {
        const startLabel = new Date(start).toLocaleDateString(options.locale, dateOptions);
        if (!options.end)
            return startLabel;
        const endLabel = new Date(options.end).toLocaleDateString(options.locale, dateOptions);
        return `${startLabel} – ${endLabel}`;
    }
    const dateTimeOptions = {
        ...dateOptions,
        hour: '2-digit',
        minute: '2-digit',
        ...(timeZone ? { timeZoneName: 'short' } : {}),
    };
    const startLabel = new Date(start).toLocaleString(options === null || options === void 0 ? void 0 : options.locale, dateTimeOptions);
    if (!(options === null || options === void 0 ? void 0 : options.end))
        return startLabel;
    const endLabel = new Date(options.end).toLocaleString(options.locale, dateTimeOptions);
    return `${startLabel} – ${endLabel}`;
};
